import axios from 'axios';

// Use relative URL for API (will use current domain)
export const API = '/api';
export const AUTH_USER_UPDATED_EVENT = 'auth-user-updated';

export const api = axios.create({
  baseURL: API,
});

let isRefreshing = false;
let refreshSubscribers = [];

const subscribeTokenRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

const onRefreshed = (newToken) => {
  refreshSubscribers.forEach((callback) => callback(newToken));
  refreshSubscribers = [];
};

const clearSession = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
  window.dispatchEvent(new Event(AUTH_USER_UPDATED_EVENT));
};

export const setSession = ({ token, refreshToken }) => {
  if (token) localStorage.setItem('token', token);
  if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
};

export const setStoredUser = (user) => {
  if (user) {
    localStorage.setItem('user', JSON.stringify(user));
  } else {
    localStorage.removeItem('user');
  }
  window.dispatchEvent(new Event(AUTH_USER_UPDATED_EVENT));
};

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config || {};
    const status = error?.response?.status;
    const detail = String(error?.response?.data?.detail || '').toLowerCase();
    const isAuthError = status === 401 && (detail.includes('token expired') || detail.includes('invalid token') || detail.includes('not authenticated'));
    const isRefreshCall = String(originalRequest?.url || '').includes('/auth/refresh');

    if (isAuthError) {
      const refreshToken = localStorage.getItem('refresh_token');

      if (!isRefreshCall && !originalRequest._retry && refreshToken) {
        if (isRefreshing) {
          return new Promise((resolve) => {
            subscribeTokenRefresh((newToken) => {
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              resolve(api(originalRequest));
            });
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const refreshResponse = await axios.post(`${API}/auth/refresh`, { refresh_token: refreshToken });
          const newToken = refreshResponse?.data?.token;
          const newRefreshToken = refreshResponse?.data?.refresh_token;

          if (!newToken || !newRefreshToken) {
            throw new Error('Missing refreshed tokens');
          }

          setSession({ token: newToken, refreshToken: newRefreshToken });
          onRefreshed(newToken);

          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          clearSession();
          window.location.replace('/login?session=expired');
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      clearSession();
      window.location.replace('/login?session=expired');
    }

    return Promise.reject(error);
  }
);

export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem('token', token);
  } else {
    clearSession();
  }
};

export const logout = async () => {
  try {
    await api.post('/auth/logout');
  } catch (_err) {
    // Ignore logout API errors and always clear local session.
  } finally {
    clearSession();
    window.location.replace('/');
  }
};
